import { EventEmitter, Injectable } from '@angular/core';
import { IBPProfile, IBPProfileBasic } from '@type_definitions/datatypes';
import * as _ from 'underscore';
import { BPProfilesDataService } from './bp-profiles-data.service';

import { Observable, Subject } from 'rxjs';

export enum BPProfileListType {
  MY_PROFILE = 'mybpprofiles',
  OTHER = 'otherbpprofiles',
  DEFAULT = 'bpprofiles'
}

export enum BPProfileSecuredDomain {
  GENERAL = 'bpprofile',
  INTERNAL = 'bpprofile-internal',
  READ = 'bpprofile-read'
}

export enum BPProfileWizardAction {
  EDIT = 'edit',
  SETUP_WIZARD = 'setup_wizard',
  ACTIONS = 'actions',
  CANCEL = 'cancel',
  SAVE = 'save',
  PRINT_OVERVIEW = 'print_overview',
  ACTIVATION_FORM = 'activation_form'
}

export enum BPProfilesViewCode {
  GENERAL_INFO = 'bp-general',
  BANK_INFO = 'bp-bank',
  ACCESS_SETTINGS = 'bp-access-settings',
  CONTACTS = 'bp-contacts',
  LOCATIONS = 'bp-locations',
  SLA = 'bp-sla',
  TC = 'bp-tc',
  CAVEATS = 'bp-caveats',
  OEM = 'bp-oem',
  CATALOG = 'bp-catalog',
  ACCREDITATIONS = 'bp-accreditations',
  DOCUMENTS = 'bp-documents',
  ACSL_INFO = 'bp-acsl-info',
  MAILS = 'bp-mails',
  TICKETS = 'bp-tickets',
  CONTRACT = 'bp-contract',
  PRODUCT_CATALOG = 'bp-product-catalog',
  LESSOR_CONFIG = 'bp-lessor-config',
  PLATFORM_CONFIG = 'bp-platform-config',
  SURVEY = 'bp-survey',
  LBDM_CHECKLIST = 'bp-lbdm-checklist',
  ORDER_KPI = 'bp-dashboard-order',
  BP_KPI = 'bp-dashboard-bp',
  AUDIT = 'bp-audit',
  INVOICE = 'bp-invoice-templates',
  TERRITORIES = 'bp-territories',
  PROFILE_CHECKLISTS = 'bp-profile-checklist',
  INTERNAL_CONFIG = 'bp-internal-config'
}

export interface IBPProfileUrlReplaceResult {
  regex:RegExp;
  result:string;
}

export interface IStatusIconLegend {
  iconClass:string;
  label:string;
}

export type EditConfirmationDialogFn = () => Promise<boolean>;

@Injectable()
export class BPProfilesService {
  private onDisableProfileSelectionSubject:Subject<boolean> = new Subject();
  private _allProfiles:IBPProfileBasic[];
  private _activeProfile:IBPProfileBasic;
  private _editingMode:boolean = false;
  private _profileDetail:IBPProfile;
  private _editConfirmationDialogFn:EditConfirmationDialogFn;

  public onActiveProfileChange:EventEmitter<IBPProfileBasic> = new EventEmitter<IBPProfileBasic>();
  public onEditChange:EventEmitter<boolean> = new EventEmitter<boolean>();
  public onCancel:EventEmitter<void> = new EventEmitter<void>();
  public onSave:EventEmitter<void> = new EventEmitter<void>();
  public onSaveBusy:EventEmitter<boolean> = new EventEmitter<boolean>();
  public onSavingError:EventEmitter<void> = new EventEmitter<void>();
  public onDisableProfileSelection:Observable<boolean> = this.onDisableProfileSelectionSubject.asObservable();

  constructor(
    private bpProfilesDataService:BPProfilesDataService
  ) {
    /**/
  }

  public get allProfiles():IBPProfileBasic[] {
    return this._allProfiles;
  }

  public set allProfiles(listProfiles:IBPProfileBasic[]) {
    this._allProfiles = listProfiles;
  }

  public get activeProfile():IBPProfileBasic {
    return this._activeProfile;
  }

  public set activeProfile(profile:IBPProfileBasic) {
    this._activeProfile = _.clone(profile);
    this.onActiveProfileChange.emit(profile);
  }

  public get editingMode():boolean {
    return this._editingMode;
  }

  public set editingMode(editingMode:boolean) {
    this._editingMode = editingMode;
    this.onEditChange.emit(editingMode);
  }

  public get bpProfileUrlReplace():IBPProfileUrlReplaceResult {
    // make sure the extra / is in the url, so that people can use
    // e.g. /bpprofiles or /bpprofiles/access-settings
    return {
      regex: /^\/((?:my|other|)bpprofiles)((?!\/(?:cc:)?[0-9]+)(?:$|\/\w|\?))/,
      result: '/$1/$2'
    };
  }

  public get profileDetail():IBPProfile {
    return this._profileDetail;
  }

  public set profileDetail(profileDetail:IBPProfile) {
    this._profileDetail = profileDetail;
  }

  public loadProfileDetails():Promise<IBPProfile> {
    if (!this.profileDetail) {
      return this.bpProfilesDataService.getBPProfileDetail(this.activeProfile?.id)
        .then((profileDetail:IBPProfile) => {
          this.profileDetail = profileDetail;
          return this.profileDetail;
        });
    } else {
      return Promise.resolve(this.profileDetail);
    }
  }

  public disableProfileSelection(disable:boolean):void {
    this.onDisableProfileSelectionSubject.next(disable);
  }

  public editConfirmationDialogFn(fn?:EditConfirmationDialogFn):Promise<boolean> | void {
    if (fn) {
      this._editConfirmationDialogFn = fn;
      return;
    }

    return this._editConfirmationDialogFn();
  }

  public clear():void {
    this.activeProfile = undefined;
    this.allProfiles = undefined;
    this.editingMode = false;
    this.profileDetail = undefined;
    this._editConfirmationDialogFn = undefined;
  }
}